import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { cn } from '../ui/Table';
import { Badge } from '../ui/arc/badge';
import { DataGrid, GridCol } from '../ui/DataGrid';
import { KpiCard, MonthPicker, MoneyField, Panel, money, signed, toneText } from './costsUi';

// Pestaña Resultados de Costos & BI (P&L mensual) — SOLO presentación.
// Todos los números vienen del useMemo `resultados` de Costs.tsx, sin tocar.

type ProductRow = { name: string; units: number; net: number; cost: number; covered: boolean; orderIds: string[] };

export interface ResultadosData {
    mOrders: number; revenueGross: number; revenueNet: number; ivaDebito: number; cmv: number; grossMargin: number;
    feesCard: number; feesClean: number; feesTN: number; feesIIBB: number; shippingOwn: number; shippingCabify: number; contribution: number;
    fixedSinMkt: number; marketing: number; marketingFull: number; marketingDaily: number; elapsedDaysMonth: number;
    expByCat: Map<string, number>; expensesTotal: number; result: number; coverage: number;
    productRows: ProductRow[]; toCost: ProductRow[];
    cmRatio: number; adsRatio: number; simRatio: number; contributionAfterAds: number; roas: number | null; isCurrentMonth: boolean; elapsedFrac: number; daysInMonth: number; projGross: number; projResult: number;
    breakEven: number; monthlyCosts: number; projCurve: number | null; projWeekly: number | null; projLinear: number;
    suiteGross: number; feesSuite: number;
    /** WhatsApp (Meta) del mes en pesos; counts null mientras carga. */
    /** Pauta: 'ads' = gasto real de adhub (ad_spend); 'fijo' = monto mensual de cost_fixed prorrateado. */
    canjeCost: number; canjeFull: number; canjeCount: number; canjeList: number; feesML: number; fixedFromHistory: boolean;
    marketingSource: 'ads' | 'fijo'; adsMeta: number; adsGoogle: number; adsThroughDay: number;
    waCost: number; waCostUSD: number | null; waCounts: { marketing: number; utility: number; service: number } | null; waNewModel: boolean;
}

// Mismo formato que tenía esta línea (fmtARS de Costs.tsx, con centavos).
const fmt2 = (n: number) => n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CAT_LABELS: Record<string, string> = { locacion: 'Locación', sueldos: 'Sueldos', marketing: 'Marketing', servicios_digitales: 'Servicios digitales', insumos: 'Insumos / Mercadería', logistica: 'Logística / Envíos', impuestos: 'Impuestos / Contador', mantenimiento: 'Mantenimiento', otros: 'Otros' };

/** Últimos 12 meses, más nuevo primero (mismo rango que antes). */
function last12(): string[] {
    const months: string[] = [];
    for (let i = 0; i < 12; i++) {
        const d = new Date(); d.setMonth(d.getMonth() - i);
        months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return months;
}

/**
 * Línea del estado de resultados. La barrita es el peso de la línea sobre la facturación neta
 * (una sola serie, un solo tono: magnitud, no identidad), con el valor exacto en el tooltip.
 */
function PLRow({ label, value, neg, kind = 'line', indent, tone, pct, bar, hint }: {
    label: React.ReactNode; value: number; neg?: boolean; kind?: 'line' | 'total' | 'result'; indent?: boolean;
    tone?: 'profit' | 'loss'; pct?: string | null; bar?: number | null; hint?: React.ReactNode;
}) {
    const isTotal = kind !== 'line';
    return (
        <div className={cn(
            'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 sm:grid-cols-[minmax(0,1fr)_auto_7.5rem]',
            isTotal ? 'my-1 rounded-[var(--radius-control)] px-3 py-2.5' : 'px-3 py-[7px]',
            kind === 'total' && 'bg-[var(--surface-muted)]',
            kind === 'result' && (tone === 'loss'
                ? 'bg-[color-mix(in_oklab,var(--danger)_9%,var(--card))]'
                : 'bg-[color-mix(in_oklab,var(--success)_9%,var(--card))]'),
        )}>
            <div className={cn('min-w-0', indent && 'pl-4')}>
                <span className={cn(
                    isTotal ? 'text-[length:var(--text-sm)] font-semibold' : 'text-[length:var(--text-sm)]',
                    indent ? 'text-soft' : 'text-ink',
                    tone && toneText(tone),
                )}>{label}</span>
                {hint && <div className="text-[length:var(--text-xs)] text-faint">{hint}</div>}
            </div>
            <span className={cn('whitespace-nowrap text-right tabular-nums',
                isTotal ? 'text-[length:var(--text-base)] font-semibold' : 'text-[length:var(--text-sm)]',
                tone ? toneText(tone) : indent ? 'text-soft' : 'text-ink')}>
                {neg && value > 0 ? '−' : ''}{money(value)}
            </span>
            <span className="hidden items-center justify-end gap-2 sm:flex">
                {pct != null && <>
                    <span className="h-1.5 w-14 overflow-hidden rounded-full bg-[var(--surface-muted)]" title={`${pct} de la facturación neta`}>
                        <span className="block h-full rounded-full bg-[color-mix(in_oklab,var(--text-secondary)_55%,var(--card))]"
                            style={{ width: `${Math.min(100, Math.max(0, bar || 0))}%` }} />
                    </span>
                    <span className="w-11 text-right text-[length:var(--text-xs)] tabular-nums text-faint">{pct}</span>
                </>}
            </span>
        </div>
    );
}

// "¿Cuánto falta facturar?" (22/10/2026): solo mes en curso. Resultado neto final =
// resultado a hoy (ya tiene los fijos ENTEROS, la pauta hasta ayer y los canjes a hoy)
// + contribución de lo que falta (a la tasa del mes: CMV, IVA, comisiones, envíos)
// − la pauta que falta (ritmo diario × días que quedan) − los canjes que faltan (al ritmo del mes).
function RestOfMonthSim({ r }: { r: ResultadosData }) {
    const [rest, setRest] = useState('');
    const forecastRest = Math.max(0, Math.round(r.projGross - r.revenueGross));
    const x = Number(rest) || 0;
    const pautaRest = Math.max(0, r.marketingFull - r.marketing);
    const canjeRest = Math.max(0, r.canjeFull - r.canjeCost);
    const contribRest = x * r.cmRatio;
    const net = r.result + contribRest - pautaRest - canjeRest;
    const totalMes = r.revenueGross + x;
    const diasQuedan = r.daysInMonth - r.elapsedDaysMonth;
    return (
        <div className="mt-3 rounded-[var(--radius-control)] border border-line bg-[var(--surface-muted)] p-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[length:var(--text-xs)] font-medium uppercase tracking-wider text-faint">¿Cuánto falta facturar este mes?</div>
                    <div className="mt-1 text-[length:var(--text-sm)] text-soft">
                        Facturado a hoy <b className="tabular-nums text-ink">{money(r.revenueGross)}</b> · quedan {diasQuedan} {diasQuedan === 1 ? 'día' : 'días'}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <MoneyField className="w-48" ariaLabel="Facturación que falta" placeholder={forecastRest.toLocaleString('es-AR')} value={rest} onChange={setRest} />
                    {forecastRest > 0 && (
                        <button type="button" onClick={() => setRest(String(forecastRest))}
                            className="whitespace-nowrap rounded-[var(--radius-control)] px-2 py-1 text-[length:var(--text-xs)] font-medium text-soft hover:bg-muted hover:text-ink">
                            usar forecast
                        </button>
                    )}
                </div>
            </div>
            {x > 0 && (
                <div className="mt-3 grid gap-1 text-[length:var(--text-sm)]">
                    <div className="flex justify-between gap-4 text-soft"><span>Resultado a hoy</span><span className="tabular-nums">{signed(r.result)}</span></div>
                    <div className="flex justify-between gap-4 text-soft"><span>+ Contribución de lo que falta ({(r.cmRatio * 100).toFixed(1)}% de {money(x)})</span><span className="tabular-nums">{signed(contribRest)}</span></div>
                    {pautaRest > 0 && <div className="flex justify-between gap-4 text-soft"><span>− Pauta que falta (al ritmo actual)</span><span className="tabular-nums">{signed(-pautaRest)}</span></div>}
                    {canjeRest > 0 && <div className="flex justify-between gap-4 text-soft"><span>− Canjes que faltan (al ritmo del mes)</span><span className="tabular-nums">{signed(-canjeRest)}</span></div>}
                    <div className="flex justify-between gap-4 border-t border-line pt-1.5 font-semibold text-ink">
                        <span>Resultado neto del mes · facturando {money(totalMes)}</span>
                        <span className={cn('tabular-nums', toneText(net >= 0 ? 'profit' : 'loss'))}>{signed(net)}</span>
                    </div>
                    <div className="text-[length:var(--text-xs)] text-faint">
                        {(net / totalMes * 100).toFixed(1)}% de la facturación · los costos fijos ya están enteros en el resultado a hoy
                        {r.breakEven > 0 && <> · {totalMes >= r.breakEven ? 'arriba' : 'abajo'} del break-even ({money(r.breakEven)})</>}
                    </div>
                </div>
            )}
        </div>
    );
}

function SimCell({ title, children, muted }: { title: string; children: React.ReactNode; muted?: boolean }) {
    return (
        <div className={cn('min-w-0 rounded-[var(--radius-control)] border border-line bg-[var(--surface-muted)] p-4', muted && 'opacity-60')}>
            <div className="text-[length:var(--text-xs)] font-medium uppercase tracking-wider text-faint">{title}</div>
            {children}
        </div>
    );
}

export function ResultadosView({ r, resMonth, setResMonth, suiteValue, onSuiteChange, onSuiteCommit, simRevenue, setSimRevenue }: {
    r: ResultadosData;
    resMonth: string; setResMonth: (m: string) => void;
    suiteValue: string | number; onSuiteChange: (v: string) => void; onSuiteCommit: () => void;
    simRevenue: string; setSimRevenue: (v: string) => void;
}) {
    const months = last12();
    const pctOf = (n: number) => r.revenueNet > 0 ? n / r.revenueNet * 100 : null;
    const pctRev = (n: number) => { const p = pctOf(n); return p == null ? '—' : p.toFixed(1) + '%'; };
    const line = (value: number) => ({ pct: pctRev(value), bar: pctOf(value) });
    const covTone = r.coverage >= 80 ? 'profit' : r.coverage >= 50 ? 'warn' : 'loss';
    const projOrReal = r.isCurrentMonth ? r.projGross : r.revenueGross;
    const partialMonth = r.elapsedDaysMonth < r.daysInMonth;
    const expCats = Array.from(r.expByCat.entries()).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);

    const cols: GridCol<ProductRow & { id: string }>[] = [
        {
            key: 'name', header: 'Producto', sort: p => p.name.replace(/^\[[^\]]*\]\s*/, ''),
            render: p => (
                <span className="flex min-w-0 items-center gap-2">
                    {!p.covered && <span title="Sin costo real cargado: asume 50% del valor de venta" className="inline-block h-2 w-2 shrink-0 rounded-full bg-amber-400" />}
                    <span className="truncate">{p.name.replace(/^\[[^\]]*\]\s*/, '')}</span>
                </span>
            ),
        },
        { key: 'units', header: 'Unid.', align: 'right', sort: p => p.units, render: p => <span className="tabular-nums">{p.units}</span> },
        { key: 'net', header: 'Venta neta', align: 'right', sort: p => p.net, render: p => <span className="tabular-nums">{money(p.net)}</span> },
        {
            key: 'cost', header: 'CMV', align: 'right', sort: p => p.cost, hideOnMobile: true,
            render: p => <span className={cn('tabular-nums', p.covered ? 'text-soft' : 'text-[var(--warning)]')}>{!p.covered ? '~' : ''}{money(p.cost)}</span>,
        },
        {
            key: 'contrib', header: 'Contribución', align: 'right', sort: p => p.net - p.cost,
            render: p => { const c = p.net - p.cost; return <span className={cn('font-semibold tabular-nums', c >= 0 ? 'text-[var(--success)]' : 'text-[var(--danger)]')}>{!p.covered ? '~' : ''}{money(c)}</span>; },
        },
        {
            key: 'pct', header: '%', align: 'right', sort: p => p.net > 0 ? (p.net - p.cost) / p.net : -Infinity,
            render: p => <span className="font-semibold tabular-nums">{p.net <= 0 ? '—' : (!p.covered ? '~' : '') + ((p.net - p.cost) / p.net * 100).toFixed(0) + '%'}</span>,
        },
    ];
    const productRows = r.productRows.slice(0, 30).map(p => ({ ...p, id: p.name }));

    return (
        <div className="tw w-full min-w-0 pb-12 pt-1">
            {/* ── Encabezado ── */}
            <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
                <div className="min-w-0">
                    <h1 className="m-0 text-[length:var(--text-2xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">Estado de resultados</h1>
                    <p className="m-0 mt-1 text-[length:var(--text-sm)] text-soft">
                        {r.mOrders} pedidos · IVA solo sobre ventas con tarjeta · costos al día de cada venta{r.isCurrentMonth ? ` · día ${r.elapsedDaysMonth} de ${r.daysInMonth}` : ''}
                    </p>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                    <MonthPicker months={months} value={resMonth} onChange={setResMonth} />
                    <label className="grid gap-1" title="Venta del mes en la Suite (sin detalle por orden). Costos: 10% comisión + 3% transferencia; sin IVA ni IIBB; CMV default 50%.">
                        <span className="flex items-center gap-1 text-[length:var(--text-xs)] font-medium text-soft">Venta Suite del mes <Info size={12} className="text-faint" /></span>
                        <MoneyField ariaLabel="Venta Suite del mes" className="w-40" placeholder="0"
                            value={suiteValue} onChange={onSuiteChange} onBlur={onSuiteCommit}
                            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
                    </label>
                </div>
            </header>

            {/* ── KPIs ── */}
            <section className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <KpiCard label={r.isCurrentMonth ? 'Resultado (a hoy)' : 'Resultado del mes'} tone={r.result >= 0 ? 'profit' : 'loss'}
                    value={signed(r.result)}
                    context={r.isCurrentMonth ? <>Forecast fin de mes: <b className={toneText(r.projResult >= 0 ? 'profit' : 'loss')}>{signed(r.projResult)}</b></> : 'Facturación menos costos variables y fijos'} />
                <KpiCard label="Margen de contribución" tone={r.contribution >= 0 ? 'profit' : 'loss'}
                    value={money(r.contribution)}
                    context={<>{(r.cmRatio * 100).toFixed(1)}% de la facturación{r.marketingSource === 'ads' && <> · después de pauta <b className="text-ink">{money(r.contributionAfterAds)}</b></>}</>} />
                <KpiCard label="Facturación" value={money(r.revenueGross)}
                    context={<>Neta de IVA: <b className="text-ink">{money(r.revenueNet)}</b></>} />
                <KpiCard label="Confianza del CMV" tone={covTone} value={`${r.coverage.toFixed(0)}%`}
                    context="de la venta con costo real cargado; el resto asume 50%">
                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[var(--surface-muted)]" role="img" aria-label={`${r.coverage.toFixed(0)}% de la venta con costo real`}>
                        <div className={cn('h-full rounded-full', covTone === 'profit' ? 'bg-[var(--success)]' : covTone === 'warn' ? 'bg-[var(--warning)]' : 'bg-[var(--danger)]')}
                            style={{ width: `${Math.min(100, Math.max(0, r.coverage))}%` }} />
                    </div>
                </KpiCard>
            </section>

            {/* ── Cuenta del mes + costo real ── */}
            <div className="mb-5 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
                <Panel title="Cuenta del mes" subtitle="% sobre la facturación neta de IVA" bodyClassName="px-2 pb-4">
                    <PLRow label="Facturación total" value={r.revenueGross} />
                    {r.suiteGross > 0 && <PLRow label="de la cual Venta Suite (sin IVA)" value={r.suiteGross} indent />}
                    <PLRow label="IVA débito (tarjeta / MP y MercadoLibre)" value={r.ivaDebito} neg indent {...line(r.ivaDebito)} />
                    <PLRow label="Costo de mercadería vendida" value={r.cmv} neg {...line(r.cmv)} />
                    <PLRow label="Margen bruto" value={r.grossMargin} kind="total" />
                    <PLRow label="Comisiones tarjeta / MP (incl. cuotas)" value={r.feesCard} neg indent {...line(r.feesCard)} />
                    <PLRow label="Comisión financiera (3% transf.)" value={r.feesClean} neg indent {...line(r.feesClean)} />
                    <PLRow label="Comisión Tiendanube (1%)" value={r.feesTN} neg indent {...line(r.feesTN)} />
                    {r.feesML > 0 && <PLRow label="Comisión MercadoLibre (15%)" value={r.feesML} neg indent {...line(r.feesML)} />}
                    {r.feesSuite > 0 && <PLRow label="Comisión Suite (10% + 3% transf.)" value={r.feesSuite} neg indent {...line(r.feesSuite)} />}
                    <PLRow label="IIBB (0 por ahora)" value={r.feesIIBB} neg indent {...line(r.feesIIBB)} />
                    <PLRow label="Envíos Cabify (costo real)" value={r.shippingCabify} neg indent {...line(r.shippingCabify)} />
                    <PLRow label="Envíos correo (Andreani / punto de retiro)" value={r.shippingOwn} neg indent {...line(r.shippingOwn)} />
                    <PLRow label="WhatsApp (Meta)" value={r.waCost} neg indent {...line(r.waCost)}
                        hint={r.waCounts
                            ? <>USD {(r.waCostUSD || 0).toFixed(2)} · {r.waCounts.marketing} marketing · {r.waCounts.utility} utility · {r.waCounts.service} servicio{r.waNewModel ? ` (${Math.min(r.waCounts.service, 1000)} de 1.000 gratis)` : ' (gratis antes de oct/26)'}</>
                            : 'calculando…'} />
                    <PLRow label="Margen de contribución" value={r.contribution} kind="result" tone={r.contribution >= 0 ? 'profit' : 'loss'} />
                    {r.marketingSource === 'ads' && (<>
                        <PLRow
                            label={partialMonth ? `Pauta Meta + Google (al día ${r.adsThroughDay})` : 'Pauta Meta + Google'}
                            value={r.marketing} neg {...line(r.marketing)}
                            hint={<>Meta Ads ${fmt2(r.adsMeta)} · Google Ads ${fmt2(r.adsGoogle)}{r.roas ? <> · ROAS general {r.roas.toFixed(1)}× (toda la facturación / pauta)</> : null}
                                {partialMonth && <> · ${fmt2(r.marketingDaily)} por día · al ritmo actual ${fmt2(r.marketingFull)} el mes</>}</>} />
                        {r.canjeCount > 0 && <PLRow label={`Canjes a creadores (${r.canjeCount})`} value={r.canjeCost} neg {...line(r.canjeCost)}
                            hint={<>Costo de lo regalado · valor de lista ${fmt2(r.canjeList)}</>} />}
                        <PLRow label={r.canjeCount > 0 ? 'Contribución después de pauta y canjes' : 'Contribución después de pauta'} value={r.contributionAfterAds} kind="result" tone={r.contributionAfterAds >= 0 ? 'profit' : 'loss'} />
                    </>)}
                    <PLRow label="Costos fijos" value={r.fixedSinMkt} neg {...line(r.fixedSinMkt)}
                        hint={r.isCurrentMonth ? 'lista actual de Costos Fijos' : r.fixedFromHistory ? 'los vigentes al cierre del mes' : undefined} />
                    {r.marketingSource === 'fijo' && (
                        <PLRow
                            label={partialMonth ? `Marketing (fijo prorrateado: ${r.elapsedDaysMonth}/${r.daysInMonth} días)` : 'Marketing (fijo)'}
                            value={r.marketing} neg {...line(r.marketing)}
                            hint={<>Sin gasto real de pauta para este mes: se usa el monto fijo de Costos Fijos{partialMonth && r.marketingFull > 0 ? <> · ${fmt2(r.marketingDaily)} por día · mes completo ${fmt2(r.marketingFull)}</> : null}</>} />
                    )}
                    {r.marketingSource === 'fijo' && r.canjeCount > 0 && <PLRow label={`Canjes a creadores (${r.canjeCount})`} value={r.canjeCost} neg {...line(r.canjeCost)}
                        hint={<>Costo de lo regalado · valor de lista ${fmt2(r.canjeList)}</>} />}
                    <PLRow label="Resultado operativo" value={r.result} kind="result" tone={r.result >= 0 ? 'profit' : 'loss'} />

                    {r.expensesTotal > 0 && (
                        <details className="group mx-3 mt-2 rounded-[var(--radius-control)] border border-dashed border-line px-3 py-2">
                            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-[length:var(--text-xs)] text-faint">
                                <span>Gastos del mes cargados — <b className="font-medium">excluidos de la cuenta por ahora</b> · ver por categoría</span>
                                <span className="tabular-nums">{money(r.expensesTotal)}</span>
                            </summary>
                            <div className="mt-2 grid gap-1">
                                {expCats.map(([cat, v]) => (
                                    <div key={cat} className="flex justify-between gap-3 text-[length:var(--text-xs)] text-soft">
                                        <span>{CAT_LABELS[cat] || cat}</span><span className="tabular-nums">{money(v)}</span>
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}
                    <p className="m-0 mx-3 mt-3 text-[length:var(--text-xs)] leading-relaxed text-faint">
                        CMV con los costos vigentes el día de cada venta: subir un insumo no cambia los meses ya vendidos (sin costo cargado: 50% del valor de venta). Fijos de la pestaña Costos Fijos; gastos por fecha y categoría.
                    </p>
                </Panel>

                <Panel
                    title={<span className="flex items-center gap-2">{r.toCost.length > 0 ? <AlertTriangle size={16} className="text-[var(--warning)]" /> : <CheckCircle2 size={16} className="text-[var(--success)]" />}Cargar costo real</span>}
                    subtitle="Top ventas del mes que hoy asumen 50% — cargalos con 🧮 en Mayorista o en Productos."
                    right={r.toCost.length > 0 ? <Badge tone="warning" size="sm">{r.toCost.length}</Badge> : undefined}
                    bodyClassName="px-0 pb-2 pt-2">
                    {r.toCost.length === 0 ? (
                        <p className="m-0 px-5 pb-3 text-[length:var(--text-sm)] font-medium text-[var(--success)]">✓ Todo lo vendido tiene costo real.</p>
                    ) : (
                        <ul className="m-0 list-none p-0">
                            {r.toCost.map(p => (
                                <li key={p.name} className="flex items-baseline justify-between gap-3 border-t border-[var(--border-subtle)] px-5 py-2 first:border-t-0">
                                    <div className="min-w-0">
                                        <div className="truncate text-[length:var(--text-sm)] text-ink">{p.name}</div>
                                        <div className="truncate text-[length:var(--text-xs)] text-faint" title={p.orderIds.join(', ')}>
                                            {p.orderIds.slice(0, 4).join(' · ')}{p.orderIds.length > 4 ? ` · +${p.orderIds.length - 4} más` : ''}
                                        </div>
                                    </div>
                                    <span className="whitespace-nowrap text-[length:var(--text-sm)] tabular-nums text-soft">{money(p.net)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </Panel>
            </div>

            {/* ── Simulador ── */}
            <Panel className="mb-5 border-dashed"
                title="Simulador"
                subtitle={r.marketingSource === 'ads'
                    ? <>Contribución después de pauta <b className="text-soft">{(r.simRatio * 100).toFixed(1)}%</b> (pauta {(r.adsRatio * 100).toFixed(1)}% de la venta) · costos fijos <b className="text-soft">{money(r.monthlyCosts)}</b>/mes (gastos excluidos por ahora)</>
                    : <>Margen de contribución <b className="text-soft">{(r.cmRatio * 100).toFixed(1)}%</b> · costos de estructura <b className="text-soft">{money(r.monthlyCosts)}</b>/mes (solo fijos — gastos excluidos por ahora)</>}
                right={<Badge size="sm">no es lo real — usa la estructura de este mes</Badge>}>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <SimCell title="Break-even">
                        {r.monthlyCosts > 0 && r.simRatio > 0 ? (
                            <>
                                <div className="mt-1.5 text-[length:var(--text-xl)] font-semibold tabular-nums text-ink">{money(r.breakEven)}</div>
                                <div className="mt-1 text-[length:var(--text-sm)] leading-snug text-soft">
                                    Facturación mensual para empatar.{' '}
                                    {r.revenueGross > 0 && (projOrReal >= r.breakEven
                                        ? <b className="text-[var(--success)]">{r.isCurrentMonth ? 'Proyectás' : 'Quedaste'} {Math.round((projOrReal / r.breakEven - 1) * 100)}% arriba ✓</b>
                                        : <b className="text-[var(--danger)]">{r.isCurrentMonth ? 'Proyectás' : 'Quedaste'} por debajo</b>)}
                                </div>
                            </>
                        ) : <div className="mt-1.5 text-[length:var(--text-sm)] text-faint">Cargá costos fijos y gastos para calcularlo</div>}
                    </SimCell>
                    <SimCell title="Forecast del mes" muted={!r.isCurrentMonth}>
                        {r.isCurrentMonth ? (
                            <>
                                <div className="mt-1.5 text-[length:var(--text-xl)] font-semibold tabular-nums text-ink">{money(r.projGross)}</div>
                                <div className="mt-1 text-[length:var(--text-sm)] leading-snug text-soft">
                                    {r.projCurve || r.projWeekly ? 'Día' : 'Run-rate a'} {(r.elapsedFrac * r.daysInMonth).toFixed(0)}/{r.daysInMonth}{r.projCurve || r.projWeekly ? '' : ' días'} → resultado estimado{' '}
                                    <b className={toneText(r.projResult >= 0 ? 'profit' : 'loss')}>{signed(r.projResult)}</b>
                                </div>
                                {(r.projCurve || r.projWeekly) && (
                                    <div className="mt-2 border-t border-line pt-2 text-[length:var(--text-xs)] leading-relaxed text-faint"
                                        title="La proyección promedia dos métodos con tu historia real: la curva de cómo se reparte la facturación dentro del mes (últimos 3 meses) y el promedio por día de semana de las últimas 8 semanas aplicado a los días que faltan. El lineal solo prorratea por días corridos.">
                                        {r.projCurve && <>curva del mes: <b className="text-soft">{money(r.projCurve)}</b> · </>}
                                        {r.projWeekly && <>ritmo semanal: <b className="text-soft">{money(r.projWeekly)}</b> · </>}
                                        lineal: {money(r.projLinear)}
                                    </div>
                                )}
                            </>
                        ) : <div className="mt-1.5 text-[length:var(--text-sm)] text-faint">Solo para el mes en curso</div>}
                    </SimCell>
                    <SimCell title="¿Y si facturo…?">
                        <MoneyField className="mt-2" ariaLabel="Facturación a simular" placeholder="60.000.000" value={simRevenue} onChange={setSimRevenue} />
                        {Number(simRevenue) > 0 && (() => {
                            const sim = Number(simRevenue);
                            const simResult = sim * r.simRatio - r.monthlyCosts;
                            return (
                                <div className="mt-2 text-[length:var(--text-sm)] leading-relaxed text-soft">
                                    Contribución{r.marketingSource === 'ads' ? ' después de pauta' : ''} <b className="tabular-nums text-ink">{money(sim * r.simRatio)}</b> → resultado{' '}
                                    <b className={cn('tabular-nums', toneText(simResult >= 0 ? 'profit' : 'loss'))}>{signed(simResult)}</b>
                                    <span className="text-faint"> ({(simResult / sim * 100).toFixed(1)}%)</span>
                                </div>
                            );
                        })()}
                    </SimCell>
                </div>
                {r.isCurrentMonth && <RestOfMonthSim r={r} />}
            </Panel>

            {/* ── Contribución por producto ── */}
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="m-0 text-[length:var(--text-base)] font-semibold text-ink">Margen de contribución por producto <span className="text-[length:var(--text-sm)] font-normal text-faint">· top 30 del mes</span></h2>
                <span className="flex items-center gap-1.5 text-[length:var(--text-xs)] text-faint"><span className="inline-block h-2 w-2 rounded-full bg-amber-400" /> ~ = sin costo real (asume 50%)</span>
            </div>
            <DataGrid rows={productRows} cols={cols} pageSize={30} dense rowKey={p => p.name}
                searchPlaceholder="Buscar producto…"
                emptyTitle="Sin ventas en el mes" emptyText="Cuando haya pedidos en este mes, acá vas a ver cuánto deja cada producto." />
        </div>
    );
}
